import React, { useState } from 'react';
import {
  Accordion,
  AccordionDetails,
  AccordionSummary,
  Box,
  Button,
  Checkbox,
  Chip,
  FormControl,
  Grid,
  IconButton,
  MenuItem,
  Select,
  TextField,
  Typography,
  Tooltip,
  makeStyles,
  InputLabel,
  FormHelperText,
} from '@material-ui/core';
import { Autocomplete } from '@material-ui/lab';
import ExpandMoreIcon from '@material-ui/icons/ExpandMore';
import DeleteIcon from '@material-ui/icons/Delete';
import AddCircleOutlineIcon from '@material-ui/icons/AddCircleOutline';
import CheckCircleIcon from '@material-ui/icons/CheckCircle';
import AddBoxIcon from '@material-ui/icons/AddBox';
import WarningIcon from '@material-ui/icons/Warning';
import CurrencyTextField from '@unicef/material-ui-currency-textfield/dist/CurrencyTextField';
import {
  formattedCurrency,
  calculateMarginByValue,
  calculateProfitByValue,
  calculateValueByMargin,
  capitalize,
  safeParseParticulars,
} from '../../../utils/utils';
import ProductUnitSelect from '../Common/ProductUnitSelect';
import { normalizeUnitCode } from '../../../constants/productUnits';

const useStyles = makeStyles((theme) => ({
  cardRoot: {
    borderRadius: '10px !important',
    marginBottom: theme.spacing(1.2),
    border: '1px solid #e2e8f0',
    boxShadow: '0 1px 3px rgba(0,0,0,0.04)',
    '&:before': { display: 'none' },
    overflow: 'hidden',
  },
  summaryRoot: {
    padding: '8px 12px !important',
    minHeight: '48px !important',
    '&.Mui-expanded': {
      minHeight: '48px !important',
    },
  },
  summaryContent: {
    margin: '4px 0 !important',
    width: '100%',
    minWidth: 0,
    '&.Mui-expanded': {
      margin: '4px 0 !important',
    },
  },
  modeSegmented: {
    display: 'flex',
    borderRadius: 6,
    border: '1px solid #cbd5e1',
    overflow: 'hidden',
    marginTop: 6,
  },
  modeBtn: {
    flex: 1,
    padding: '8px 6px',
    fontSize: '0.75rem',
    cursor: 'pointer',
    border: 'none',
    backgroundColor: '#ffffff',
    color: '#475569',
    fontWeight: 500,
    outline: 'none',
    textAlign: 'center',
    '&.active': {
      backgroundColor: '#17497F',
      color: '#ffffff',
      fontWeight: 600,
    },
  },
  formatChip: {
    backgroundColor: '#f8fafc',
    border: '1px solid #cbd5e1',
    borderRadius: 6,
    padding: theme.spacing(1),
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'space-between',
    marginTop: 6,
  },
}));

const ImportCardMobile = ({
  product,
  index,
  resolution = {},
  isSelected,
  onToggleSelect,
  onRemove,
  onChangeResolution,
  categoryList = [],
  onOpenFormatModal,
}) => {
  const classes = useStyles();
  const [expanded, setExpanded] = useState(false);

  const mode = resolution.conversionMode || (product.isPackage ? 'FRACTION_ONLY' : 'SIMPLE');
  const itemsPerUnit = resolution.itemsPerUnit || product.suggestedItemsPerUnit || 1;
  const currentUnit = normalizeUnitCode(resolution.unit || (mode === 'FRACTION_ONLY' ? 'UN' : (product.unit || 'UN')));

  let displayAmount = product.amount;
  let unitCost = product.cost;

  if (mode === 'FRACTION_ONLY') {
    displayAmount = product.amount * itemsPerUnit;
    unitCost = product.cost / itemsPerUnit;
  }

  const saleValue = resolution.value !== undefined ? resolution.value : calculateValueByMargin(unitCost, resolution.margin || 30);
  const margin = resolution.margin !== undefined ? resolution.margin : calculateMarginByValue(unitCost, saleValue);
  const profit = calculateProfitByValue(unitCost, saleValue);

  const childFormats = resolution.childFormats || [];

  const isExisting = product.analysis?.status === 'EXISTING';
  const isAdoptedAsChild = mode === 'PARENT_CHILD' && childFormats.some(
    (f) => String(f.id) === String(product.analysis?.product?.id) || f.wasAdoptedAsChild,
  );
  const currentAction = isAdoptedAsChild
    ? 'NEW'
    : (resolution.action || (isExisting ? 'ADD' : 'NEW'));
  const isAddAction = currentAction === 'ADD';

  // Preservação da categoria para produto existente (ação ADD)
  const preservedCategoryId = isExisting
    ? (product.analysis?.product?.category || resolution.category)
    : resolution.category;
  const currentCategoryId = isAddAction ? preservedCategoryId : resolution.category;
  const selectedCategory = categoryList.find((c) => c.id === currentCategoryId) || categoryList[0] || null;

  // Particularidades da categoria para produto novo
  const categoryParticularsDef = !isAddAction ? safeParseParticulars(selectedCategory?.particulars) : {};
  const hasParticulars = Object.keys(categoryParticularsDef).length > 0;

  const handleModeChange = (newMode) => {
    let updates = { conversionMode: newMode };
    const isExisting = product.analysis?.status === 'EXISTING';
    const existing = isExisting ? product.analysis.product : null;
    const existingHasFormats = existing?.childFormats && existing.childFormats.length > 0;

    if (newMode === 'FRACTION_ONLY') {
      const factor = itemsPerUnit > 1 ? itemsPerUnit : (product.suggestedItemsPerUnit || 12);
      updates.itemsPerUnit = factor;
      const newUnitCost = product.cost / factor;
      updates.value = isExisting && existing?.value ? existing.value : calculateValueByMargin(newUnitCost, resolution.margin || 30);
      updates.childFormats = [];
      if (isExisting && !existingHasFormats) {
        updates.action = 'ADD';
        updates.productId = existing.id;
        updates.customName = existing.name || product.name;
      }
    } else if (newMode === 'PARENT_CHILD') {
      updates.itemsPerUnit = 1;
      const factor = itemsPerUnit > 1 ? itemsPerUnit : (product.suggestedItemsPerUnit || 1);

      if (isExisting && existingHasFormats) {
        // O produto existente já era um Pai (Caixa com formatos cadastrados)
        updates.action = 'ADD';
        updates.productId = existing.id;
        updates.value = existing.value;
      } else if (isExisting && !existingHasFormats) {
        // O produto existente era a UNIDADE avulsa!
        // A Caixa da NFe entra como novo Pai (action = 'NEW')
        // E o produto existente é adotado como formato filho com suas configurações atuais
        updates.action = 'NEW';
        updates.productId = undefined;
        updates.customName = product.name;
        updates.value = calculateValueByMargin(product.cost, resolution.margin || 30);

        const unitCost = factor > 0 ? product.cost / factor : product.cost;
        updates.childFormats = [
          {
            id: existing.id,
            name: existing.name,
            fractionFactor: factor > 1 ? factor : 12,
            value: existing.value !== undefined ? existing.value : calculateValueByMargin(unitCost, resolution.margin || 30),
            barCode: existing.barCode || '',
            cost: Math.round((unitCost + Number.EPSILON) * 10000) / 10000,
            isExisting: true,
            wasAdoptedAsChild: true,
            previousAmount: existing.amount || 0,
          },
        ];
      } else {
        // Produto novo que não existia na base
        updates.action = 'NEW';
        updates.value = calculateValueByMargin(product.cost, resolution.margin || 30);

        if ((!resolution.childFormats || resolution.childFormats.length === 0) && (product.isPackage || factor > 1)) {
          if (factor > 1) {
            const cleanName = (resolution.customName || product.name || '')
              .replace(/\b\d{1,3}\s*(?:PACKS?|PKS?|PCKS?|FL|FILM|FILME|U|UN|UNID|LATAS?)\b/gi, '')
              .replace(/\b(?:CX|FD|PCT|LT|PET)\d{1,3}\b/gi, '')
              .replace(/\b(?:CX|FD|PCT|FARDO|CAIXA|PACK|FL)\b/gi, '')
              .replace(/\s+/g, ' ')
              .trim();
            const uCost = product.cost / factor;
            updates.childFormats = [
              {
                name: cleanName || 'Unidade Avulsa',
                fractionFactor: factor,
                value: calculateValueByMargin(uCost, resolution.margin || 30),
                barCode: '',
                isAutoSuggested: true,
              },
            ];
          }
        }
      }
    } else {
      // SIMPLE (Vender Fechado)
      updates.itemsPerUnit = 1;
      updates.childFormats = [];
      updates.value = calculateValueByMargin(product.cost, resolution.margin || 30);
      if (isExisting && !existingHasFormats) {
        updates.action = 'NEW';
        updates.productId = undefined;
      }
    }
    onChangeResolution(index, updates);
  };

  const handleValueChange = (newVal) => {
    const newMargin = calculateMarginByValue(unitCost, newVal);
    onChangeResolution(index, { value: newVal, margin: newMargin });
  };

  return (
    <Accordion
      className={classes.cardRoot}
      expanded={expanded}
      onChange={() => setExpanded(!expanded)}
    >
      <AccordionSummary
        expandIcon={<ExpandMoreIcon />}
        classes={{
          root: classes.summaryRoot,
          content: classes.summaryContent,
        }}
      >
        <Box style={{ width: '100%', minWidth: 0 }}>
          {/* Linha 1: Checkbox + Nome do Produto com 100% de largura disponível */}
          <Box display="flex" alignItems="center" style={{ width: '100%', minWidth: 0 }}>
            <Checkbox
              size="small"
              checked={isSelected}
              onClick={(e) => e.stopPropagation()}
              onChange={() => onToggleSelect(index)}
              style={{ padding: 2, marginRight: 6, flexShrink: 0 }}
            />
            <Box style={{ flex: 1, minWidth: 0 }}>
              <Tooltip title={resolution.customName || product.name || ''} enterTouchDelay={300}>
                <Typography
                  variant="body2"
                  style={{
                    fontWeight: 600,
                    fontSize: '0.85rem',
                    lineHeight: 1.25,
                    whiteSpace: 'nowrap',
                    overflow: 'hidden',
                    textOverflow: 'ellipsis',
                    display: 'block',
                    color: '#1e293b',
                  }}
                >
                  {resolution.customName || product.name}
                </Typography>
              </Tooltip>
            </Box>
          </Box>

          {/* Linha 2: Quantidade & Custo à esquerda, Preço & Margem à direita */}
          <Box
            display="flex"
            alignItems="center"
            justifyContent="space-between"
            mt={0.6}
            pl="30px"
            style={{ width: '100%', minWidth: 0 }}
          >
            <Box display="flex" alignItems="center" gap={0.6} flexWrap="wrap" style={{ minWidth: 0, flex: 1 }}>
              <Typography variant="caption" color="textSecondary" style={{ fontSize: '0.72rem', whiteSpace: 'nowrap' }}>
                Qtd: <strong>{displayAmount} {currentUnit}</strong> • Custo: <strong>{formattedCurrency(unitCost)}</strong>
              </Typography>
              {product.isPackage && (
                <Chip
                  size="small"
                  label={mode === 'FRACTION_ONLY' ? `x${itemsPerUnit} un` : mode === 'PARENT_CHILD' ? `+${childFormats.length} frações` : 'Coletivo'}
                  style={{ height: 16, fontSize: '0.62rem', backgroundColor: '#e0f2fe', color: '#0369a1', fontWeight: 600 }}
                />
              )}
            </Box>

            <Box textAlign="right" style={{ flexShrink: 0, marginLeft: 8 }}>
              <Typography variant="body2" style={{ fontWeight: 700, color: '#17497F', fontSize: '0.86rem', lineHeight: 1.1, whiteSpace: 'nowrap' }}>
                {formattedCurrency(saleValue)}
              </Typography>
              <Typography variant="caption" color="textSecondary" style={{ fontSize: '0.68rem', display: 'block', whiteSpace: 'nowrap' }}>
                {Number(margin || 0).toFixed(0)}% margem
              </Typography>
            </Box>
          </Box>
        </Box>
      </AccordionSummary>

      <AccordionDetails style={{ flexDirection: 'column', paddingTop: 0, paddingBottom: 16 }}>
        <Grid container spacing={2}>
          {/* Nome e Unidade Editáveis */}
          <Grid item xs={8}>
            <TextField
              fullWidth
              size="small"
              label="Nome do Produto"
              variant="outlined"
              value={resolution.customName !== undefined ? resolution.customName : product.name}
              onChange={(e) => onChangeResolution(index, 'customName', e.target.value)}
            />
          </Grid>
          <Grid item xs={4}>
            <ProductUnitSelect
              variant="compact"
              size="small"
              label="Unidade"
              value={currentUnit}
              onChange={(newUnit) => onChangeResolution(index, 'unit', newUnit)}
            />
          </Grid>

          {/* Modelo de Venda e Frações */}
          <Grid item xs={12}>
            <Typography variant="caption" style={{ fontWeight: 600, color: '#475569', marginBottom: 4, display: 'block' }}>
              Modo de Venda
            </Typography>
            <div className={classes.modeSegmented}>
              <button
                type="button"
                className={`${classes.modeBtn} ${mode === 'FRACTION_ONLY' ? 'active' : ''}`}
                onClick={() => handleModeChange('FRACTION_ONLY')}
              >
                Só Fração
              </button>
              <button
                type="button"
                className={`${classes.modeBtn} ${mode === 'PARENT_CHILD' ? 'active' : ''}`}
                onClick={() => handleModeChange('PARENT_CHILD')}
              >
                Caixa + Filhos
              </button>
              <button
                type="button"
                className={`${classes.modeBtn} ${mode === 'SIMPLE' ? 'active' : ''}`}
                onClick={() => handleModeChange('SIMPLE')}
              >
                Fechado
              </button>
            </div>

            {mode === 'FRACTION_ONLY' && (
              <Box mt={1.5} p={1.5} bgcolor="#f0f9ff" borderRadius={6} border="1px solid #bae6fd">
                <Typography variant="caption" color="textSecondary" style={{ display: 'block', marginBottom: 8, fontSize: '0.75rem' }}>
                  Converte a caixa diretamente em unidades no estoque:
                </Typography>
                <TextField
                  type="number"
                  size="small"
                  fullWidth
                  label="Unidades na Caixa"
                  variant="outlined"
                  value={itemsPerUnit}
                  onChange={(e) => {
                    const factor = Math.max(1, parseInt(e.target.value, 10) || 1);
                    onChangeResolution(index, {
                      itemsPerUnit: factor,
                      value: calculateValueByMargin(product.cost / factor, margin),
                    });
                  }}
                />
              </Box>
            )}

            {mode === 'PARENT_CHILD' && (
              <Box mt={1.5}>
                <Button
                  size="small"
                  variant="outlined"
                  color="primary"
                  fullWidth
                  startIcon={<AddCircleOutlineIcon />}
                  onClick={() => onOpenFormatModal(
                    index,
                    product.cost,
                    resolution.customName || product.name,
                    itemsPerUnit || product.suggestedItemsPerUnit
                  )}
                >
                  Adicionar Formato Filho
                </Button>

                {childFormats.map((fmt, fIdx) => {
                  const calculatedCost = product.cost / (fmt.fractionFactor || 1);
                  const childValue = parseFloat(fmt.value) || 0;
                  const childProfit = childValue - calculatedCost;
                  const childMargin = childValue > 0 ? ((childProfit / childValue) * 100) : 0;
                  return (
                    <div key={fIdx} className={classes.formatChip} style={{ padding: '8px 10px', alignItems: 'center', marginTop: 8 }}>
                      <Box style={{ flex: 1, minWidth: 0, marginRight: 8 }}>
                        <Box display="flex" alignItems="center" gap={0.5} style={{ minWidth: 0 }}>
                          <Tooltip title={fmt.name || ''} enterTouchDelay={300}>
                            <Typography
                              variant="caption"
                              style={{
                                fontWeight: 600,
                                whiteSpace: 'nowrap',
                                overflow: 'hidden',
                                textOverflow: 'ellipsis',
                                display: 'block',
                              }}
                            >
                              {fmt.name}
                            </Typography>
                          </Tooltip>
                          {fmt.wasAdoptedAsChild ? (
                            <Chip
                              size="small"
                              label={`Unidade #${fmt.id} Vinculada`}
                              style={{ height: 16, fontSize: '0.62rem', backgroundColor: '#eff6ff', color: '#17497F', fontWeight: 600 }}
                            />
                          ) : fmt.isExisting ? (
                            <Chip
                              size="small"
                              label="Existente"
                              style={{ height: 16, fontSize: '0.65rem', backgroundColor: '#dcfce7', color: '#166534' }}
                            />
                          ) : null}
                        </Box>
                        <Typography variant="caption" color="textSecondary" display="block">
                          Rendimento: <strong>1/{fmt.fractionFactor}</strong> • Custo: <strong>{formattedCurrency(calculatedCost)}</strong>
                        </Typography>
                        <Typography variant="caption" style={{ color: childProfit >= 0 ? '#16a34a' : '#dc2626', fontSize: '0.7rem', display: 'block', fontWeight: 600 }}>
                          Margem: {childMargin.toFixed(1)}% ({formattedCurrency(childProfit)})
                        </Typography>
                        {fmt.wasAdoptedAsChild && fmt.previousAmount > 0 && (
                          <Typography variant="caption" style={{ color: '#059669', fontSize: '0.68rem', display: 'block' }}>
                            +{fmt.previousAmount} un. migradas ({Math.round(((fmt.previousAmount / (fmt.fractionFactor || 1)) + Number.EPSILON) * 100) / 100} cx) p/ pai
                          </Typography>
                        )}
                      </Box>
                      <Box display="flex" alignItems="center" gap={0.5}>
                        <CurrencyTextField
                          size="small"
                          variant="outlined"
                          decimalCharacter=","
                          digitGroupSeparator="."
                          currencySymbol="R$"
                          outputFormat="number"
                          value={fmt.value}
                          onChange={(e, val) => {
                            const updated = [...childFormats];
                            updated[fIdx] = { ...updated[fIdx], value: val };
                            onChangeResolution(index, 'childFormats', updated);
                          }}
                          style={{ width: 85 }}
                        />
                        {!fmt.isExisting && !fmt.id && (
                          <IconButton
                            size="small"
                            onClick={() => {
                              const updated = childFormats.filter((_, i) => i !== fIdx);
                              onChangeResolution(index, 'childFormats', updated);
                            }}
                          >
                            <DeleteIcon fontSize="small" style={{ color: '#ef4444' }} />
                          </IconButton>
                        )}
                      </Box>
                    </div>
                  );
                })}
              </Box>
            )}
          </Grid>

          {/* Categoria */}
          <Grid item xs={12}>
            <Autocomplete
              disableClearable
              size="small"
              disabled={isAddAction}
              value={selectedCategory}
              options={categoryList}
              getOptionLabel={(option) => option.name}
              getOptionSelected={(option, value) => option.id === value?.id}
              onChange={(e, newVal) => {
                if (newVal) {
                  onChangeResolution(index, {
                    category: newVal.id,
                    particulars: {},
                  });
                }
              }}
              renderInput={(params) => (
                <TextField
                  {...params}
                  variant="outlined"
                  label="Categoria"
                />
              )}
            />
            {isAddAction ? (
              <FormHelperText style={{ marginTop: 4, marginLeft: 2, fontSize: '0.72rem', color: '#64748b' }}>
                Categoria preservada do produto existente
              </FormHelperText>
            ) : isAdoptedAsChild ? (
              <FormHelperText style={{ marginTop: 4, marginLeft: 2, fontSize: '0.72rem', color: '#64748b' }}>
                Categoria da nova embalagem mestre (Caixa)
              </FormHelperText>
            ) : null}
          </Grid>

          {/* Ação ao Confirmar para produtos existentes/similares */}
          {product.analysis?.status !== 'NEW' && (
            <Grid item xs={12}>
              <FormControl variant="outlined" size="small" fullWidth>
                <Select
                  value={currentAction}
                  onChange={(e) => {
                    const newAction = e.target.value;
                    if (newAction === 'ADD') {
                      onChangeResolution(index, {
                        action: 'ADD',
                        category: preservedCategoryId,
                        particulars: {},
                      });
                    } else {
                      onChangeResolution(index, 'action', newAction);
                    }
                  }}
                >
                  {product.analysis?.product && (
                    <MenuItem
                      value="ADD"
                      disabled={isAdoptedAsChild}
                    >
                      {isAdoptedAsChild
                        ? `Somar ao Estoque (Indisponível: #${product.analysis.product.id} é a fração abaixo)`
                        : `Somar ao Estoque Existente (ID: ${product.analysis.product.id})`}
                    </MenuItem>
                  )}
                  <MenuItem value="NEW">Cadastrar como Novo Produto</MenuItem>
                </Select>
              </FormControl>
              {isAdoptedAsChild && (
                <Typography variant="caption" color="textSecondary" style={{ display: 'block', marginTop: 4, fontStyle: 'italic', fontSize: '0.72rem' }}>
                  O produto #{product.analysis.product.id} foi vinculado como fração de venda. A caixa da nota entrará como novo produto pai.
                </Typography>
              )}
            </Grid>
          )}

          {/* Controle de Particularidades para Produto Novo */}
          {!isAddAction && hasParticulars && (
            <Grid item xs={12}>
              <Box
                p={1.5}
                style={{
                  backgroundColor: '#f8fbff',
                  border: '1px solid #d4e5f9',
                  borderRadius: 6,
                }}
              >
                <Typography
                  variant="caption"
                  style={{
                    fontWeight: 700,
                    textTransform: 'uppercase',
                    letterSpacing: 0.5,
                    marginBottom: 8,
                    display: 'block',
                    color: '#17497F',
                  }}
                >
                  Características da Categoria ({selectedCategory?.name})
                </Typography>
                <Grid container spacing={1.5}>
                  {Object.entries(categoryParticularsDef).map(([key, value]) => {
                    const currentParticulars = resolution.particulars || {};
                    const currentVal = currentParticulars[key] || '';
                    return (
                      <Grid key={key} item xs={12}>
                        <FormControl
                          variant="outlined"
                          size="small"
                          fullWidth
                          required
                          error={!currentVal}
                        >
                          <InputLabel id={`label-card-part-${index}-${key}`}>
                            {capitalize(key)} *
                          </InputLabel>
                          <Select
                            labelId={`label-card-part-${index}-${key}`}
                            label={`${capitalize(key)} *`}
                            value={currentVal}
                            onChange={(e) => {
                              const updated = {
                                ...currentParticulars,
                                [key]: e.target.value,
                              };
                              onChangeResolution(index, 'particulars', updated);
                            }}
                          >
                            <MenuItem value="">
                              <em>Selecione o(a) {key} *</em>
                            </MenuItem>
                            {Array.isArray(value)
                              ? value.map((vl) => (
                                  <MenuItem key={vl} value={vl}>
                                    {vl}
                                  </MenuItem>
                                ))
                              : value && (
                                  <MenuItem key={value} value={value}>
                                    {value}
                                  </MenuItem>
                                )}
                          </Select>
                          {!currentVal && (
                            <FormHelperText error>Campo obrigatório</FormHelperText>
                          )}
                        </FormControl>
                      </Grid>
                    );
                  })}
                </Grid>
              </Box>
            </Grid>
          )}

          {/* Preço de Venda */}
          <Grid item xs={6}>
            <CurrencyTextField
              fullWidth
              size="small"
              variant="outlined"
              label="Preço de Venda"
              decimalCharacter=","
              digitGroupSeparator="."
              currencySymbol="R$"
              outputFormat="number"
              value={saleValue}
              onChange={(e, val) => handleValueChange(val)}
            />
          </Grid>

          <Grid item xs={6}>
            <TextField
              size="small"
              label="Margem (%)"
              type="number"
              variant="outlined"
              fullWidth
              value={margin}
              onChange={(e) => {
                const newMargin = parseFloat(e.target.value) || 0;
                const newVal = calculateValueByMargin(unitCost, newMargin);
                onChangeResolution(index, { margin: newMargin, value: newVal });
              }}
            />
          </Grid>

          {/* Botão de Excluir */}
          <Grid item xs={12}>
            <Box display="flex" justifyContent="flex-end" mt={1}>
              <Button
                size="small"
                color="secondary"
                startIcon={<DeleteIcon />}
                onClick={() => onRemove(index)}
              >
                Remover da Nota
              </Button>
            </Box>
          </Grid>
        </Grid>
      </AccordionDetails>
    </Accordion>
  );
};

export default ImportCardMobile;
